import React from 'react';

import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface SentryWindow extends Window {
  Sentry?: {
    captureException: (error: Error, context?: Record<string, unknown>) => void;
  };
}

declare const window: SentryWindow;

export interface AppErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report error to Sentry if available
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback ?? ErrorBoundaryFallback;
      
      return <FallbackComponent />;
    }

    return this.props.children;
  }
}
